'use client'

import * as React from 'react'
import { ChevronDown } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { cn } from '@/utils'
import { InvalidEntryTooltip } from './components/invalid-entry-tooltip'
import { ZoomDropdown } from './components/zoom-dropdown'
import { ZOOM_LISTBOX_ID } from './constants/zoom-dropdown'
import { buildMenuOptions, formatZoomMenuAriaKeyshortcuts } from './helpers/zoom-menu-options'
import { parsePercentInput } from './helpers/zoom-percent'
import { useInvalidEntryTooltip } from './hooks/use-invalid-entry-tooltip'
import { useZoomCommit } from './hooks/use-zoom-commit'
import { useZoomDraft } from './hooks/use-zoom-draft'
import { useZoomDropdownPosition } from './hooks/use-zoom-dropdown-position'
import { useZoomHighlight } from './hooks/use-zoom-highlight'
import { useZoomInputHandlers } from './hooks/use-zoom-input-handlers'
import { useZoomMenuDismiss } from './hooks/use-zoom-menu-dismiss'

export type ZoomLevelControlProps = {
  /** Current effective zoom as percent (10–800+; values outside range are clamped for display). */
  zoomPercent: number
  /** Commit a finite zoom level (percent). Parent clamps to editor min/max. */
  onZoomPercentCommit: (percent: number) => void
  /** Set zoom to 100% and center the spread on the stage. */
  onActualSize: () => void
  /** Enter fit-to-page mode (zoom === 0 in parent). */
  onZoomFit: () => void
  /** When true, Fit to spread is shown but inactive (no document or zero-size viewport). */
  fitToSpreadDisabled?: boolean
  /** Fit the current selection in the viewport and commit an explicit zoom (percent updates in the trigger). */
  onZoomToSelection?: () => void
  /** When true, Zoom to Selection is shown but inactive (e.g. no selection). Requires `onZoomToSelection`. */
  zoomToSelectionDisabled?: boolean
  minPercent?: number
  maxPercent?: number
  className?: string
  /**
   * When set, the dropdown is portaled and positioned so its **right edge** lines up with this
   * element’s **left edge** (e.g. the right menubar cluster in the navbar).
   */
  dropdownAlignRef?: React.RefObject<HTMLElement | null>
}

/**
 * Inline list when there is no align element; portaled once its position is measured (nothing
 * for the frame before that).
 */
function resolveDropdownMode(
  dropdownAlignRef: ZoomLevelControlProps['dropdownAlignRef'],
  dropdownFixedStyle: React.CSSProperties | null
): 'portal' | 'inline' | null {
  if (!dropdownAlignRef) return 'inline'

  return dropdownFixedStyle ? 'portal' : null
}

export function ZoomLevelControl({
  zoomPercent,
  onZoomPercentCommit,
  onActualSize,
  onZoomFit,
  fitToSpreadDisabled = false,
  onZoomToSelection,
  zoomToSelectionDisabled = true,
  minPercent = 10,
  maxPercent = 800,
  className,
  dropdownAlignRef,
}: ZoomLevelControlProps) {
  const [open, setOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const listRef = React.useRef<HTMLDivElement | null>(null)
  const dropdownPanelRef = React.useRef<HTMLDivElement | null>(null)

  const menuOptions = React.useMemo(() => buildMenuOptions(onZoomToSelection), [onZoomToSelection])
  const zoomMenuAriaKeyshortcuts = React.useMemo(
    () => formatZoomMenuAriaKeyshortcuts(Boolean(onZoomToSelection)),
    [onZoomToSelection]
  )
  const zoomSelectionRowDisabled = zoomToSelectionDisabled || !onZoomToSelection

  // Hook order keeps the original passive-effect order: draft sync, close resets (position, then
  // highlight — the setup-free tooltip-timer cleanup sits between them), first-option highlight,
  // scroll highlighted row into view, outside/Escape dismissal.
  const zoomDraft = useZoomDraft({ zoomPercent, minPercent, maxPercent, menuOptions })
  const { draft, isDirty, resetDraftFromProps } = zoomDraft
  const dropdownFixedStyle = useZoomDropdownPosition({ open, dropdownAlignRef, containerRef })
  const { invalidEntryTooltipVisible, flashInvalidEntryTooltip } = useInvalidEntryTooltip()
  const { highlightIndex, setHighlightIndex, moveHighlight, highlightOption } = useZoomHighlight({
    open,
    menuOptions,
    zoomSelectionRowDisabled,
    fitToSpreadDisabled,
    applyHighlightPreview: zoomDraft.applyHighlightPreview,
    listRef,
  })
  const { commitDraft, activateOptionIndex } = useZoomCommit({
    menuOptions,
    minPercent,
    maxPercent,
    onZoomPercentCommit,
    onActualSize,
    onZoomFit,
    onZoomToSelection,
    fitToSpreadDisabled,
    zoomSelectionRowDisabled,
    draft,
    setDraft: zoomDraft.setDraft,
    setIsDirty: zoomDraft.setIsDirty,
    dirtyFromInputChangeRef: zoomDraft.dirtyFromInputChangeRef,
    resetDraftFromProps,
    flashInvalidEntryTooltip,
    setOpen,
    setHighlightIndex,
    inputRef,
  })

  useZoomMenuDismiss({
    open,
    containerRef,
    dropdownPanelRef,
    setOpen,
    setIsDirty: zoomDraft.setIsDirty,
    dirtyFromInputChangeRef: zoomDraft.dirtyFromInputChangeRef,
    resetDraftFromProps,
    setHighlightIndex,
    inputRef,
  })
  const input = useZoomInputHandlers({
    open,
    setOpen,
    highlightIndex,
    setHighlightIndex,
    optionCount: menuOptions.length,
    moveHighlight,
    activateOptionIndex,
    commitDraft,
    isDirty,
    setIsDirty: zoomDraft.setIsDirty,
    setDraft: zoomDraft.setDraft,
    dirtyFromInputChangeRef: zoomDraft.dirtyFromInputChangeRef,
    resetDraftFromProps,
    inputRef,
  })

  const parsedDraft = parsePercentInput(draft)

  /** Don’t tint a preset as “current %” while a non-preset row is hovered (e.g. Fit resets draft to 100%). */
  const suppressPresetMatchHighlight =
    highlightIndex >= 0 && menuOptions[highlightIndex]?.kind !== 'preset'

  const dropdownMode = resolveDropdownMode(dropdownAlignRef, dropdownFixedStyle)

  return (
    <div
      ref={containerRef}
      className={cn('relative inline-flex w-[100px] min-w-[100px] max-w-[100px]', className)}
    >
      <label htmlFor="zoom-level-input" className="sr-only">
        Zoom percent
      </label>
      <Input
        ref={inputRef}
        id="zoom-level-input"
        size="sm"
        inputMode="decimal"
        autoComplete="off"
        aria-expanded={open}
        aria-controls={ZOOM_LISTBOX_ID}
        aria-activedescendant={
          open && highlightIndex >= 0 ? `zoom-option-${highlightIndex}` : undefined
        }
        aria-haspopup="listbox"
        role="combobox"
        aria-keyshortcuts={zoomMenuAriaKeyshortcuts}
        value={draft}
        onFocus={input.handleInputFocus}
        onClick={input.handleInputClick}
        onMouseDown={input.handleInputMouseDown}
        onChange={input.handleInputChange}
        onBlur={input.handleInputBlur}
        onKeyDown={input.handleInputKeyDown}
        className={cn(
          'h-8 min-w-0 w-full pr-9 text-center text-sm tabular-nums font-normal shadow-none',
          isDirty ? 'text-muted-foreground' : 'text-foreground'
        )}
      />
      <button
        type="button"
        aria-label="Toggle zoom presets"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={ZOOM_LISTBOX_ID}
        className="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-muted-foreground hover:text-foreground"
        onMouseDown={(event) => event.preventDefault()}
        onClick={input.handleToggleClick}
      >
        <ChevronDown
          className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>

      {invalidEntryTooltipVisible ? (
        <InvalidEntryTooltip minPercent={minPercent} maxPercent={maxPercent} />
      ) : null}

      {open && dropdownMode && (
        <ZoomDropdown
          mode={dropdownMode}
          fixedStyle={dropdownFixedStyle ?? undefined}
          panelRef={dropdownPanelRef}
          listRef={listRef}
          menuOptions={menuOptions}
          highlightIndex={highlightIndex}
          parsedDraft={parsedDraft}
          suppressPresetMatchHighlight={suppressPresetMatchHighlight}
          fitToSpreadDisabled={fitToSpreadDisabled}
          zoomSelectionRowDisabled={zoomSelectionRowDisabled}
          onHighlight={highlightOption}
          onActivate={activateOptionIndex}
        />
      )}
    </div>
  )
}
